// Store
import store from '../store'

// Utilities
import LoggerUtil from './loggerUtil'
import { BrowserType, OsType, DesktopOsType } from '../constants'

class DeviceUtil {
  /**
   * Determine the whether user is on a mobile device or not. Source: https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
   * @returns {Boolean} True if on a mobile device.
   */
  static getIsMobileDevice () {
    const mobileDeviceTest1 = store.state.session.deviceDetection.mobileDeviceTest1
    const mobileDeviceTest2 = store.state.session.deviceDetection.mobileDeviceTest2

    const userAgent = navigator.userAgent || navigator.vendor || window.opera

    const isMobileDevice = mobileDeviceTest1.test(userAgent) || mobileDeviceTest2.test(userAgent.substr(0, 4))

    LoggerUtil.debug(`Is mobile device: ${isMobileDevice}`)

    return isMobileDevice
  }

  /**
   * Determine the whether user is on a tablet device or not.
   * @returns {Boolean} True if on a tablet device.
   */
  static getIsTabletDevice () {
    const userAgent = navigator.userAgent.toLowerCase()
    const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(userAgent)

    const platform = navigator?.userAgentData?.platform || navigator?.platform || 'unknown'
    const iPadDetect = (platform === 'MacIntel' && navigator.maxTouchPoints > 0) || platform === 'iPad'

    const isWindows = navigator.userAgent.indexOf('Windows') > -1
    const maxTouchPoints = navigator.maxTouchPoints || navigator.msMaxTouchPoints
    const isTouchable = 'ontouchstart' in window || maxTouchPoints > 0 || (window.matchMedia && matchMedia('(any-pointer: coarse)').matches)
    const surfaceDetect = isWindows && isTouchable

    return isTablet || iPadDetect || surfaceDetect
  }

  /**
   * Gets the type of browser being used.
   * @returns {String} The name of the browser detected.
   */
  static getBrowserType () {
    const userAgent = navigator.userAgent

    const iOS = userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)
    const webkit = userAgent.match(/WebKit/i)
    const iOSSafari = !window.MSStream && iOS && webkit && !userAgent.match(/CriOS/i) && !userAgent.match(/FxiOS/i)
    const isSamsung = userAgent.match(/SAMSUNG/i)

    if (iOSSafari) {
      LoggerUtil.debug(`Browser type: ${BrowserType.Safari}`)
      return BrowserType.Safari
    } else if (isSamsung) {
      LoggerUtil.debug(`Browser type: ${BrowserType.Samsung}`)
      return BrowserType.Samsung
    }

    LoggerUtil.debug(`Browser type: ${BrowserType.Unknown}`)
    return BrowserType.Unknown
  }

  /**
   * Determine the mobile operating system.
   * @returns {String} The OS name.
   */
  static getMobileOs () {
    const userAgent = navigator.userAgent || navigator.vendor

    if (!/windows phone/i.test(userAgent) && /android/i.test(userAgent)) { // Windows Phone must come first because its UA also contains 'Android'
      LoggerUtil.debug(`Operating System type: ${OsType.Android}`)
      return OsType.Android
    }

    const iOSPlatforms = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ]

    if (iOSPlatforms.includes(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)) {
      LoggerUtil.debug(`Operating System type: ${OsType.Apple}`)
      return OsType.Apple
    }

    LoggerUtil.debug(`Operating System type: ${OsType.Unknown}`)
    return OsType.Unknown
  }

  /**
   * Determine the desktop operating system.
   * @returns {String} The OS name.
   */
  static getDesktopOs () { // TODO: Refactor "getMobileOs" and "getDesktopOs" to a single "getOs" function
    const userAgent = navigator.userAgent || navigator.vendor

    if (userAgent.includes('Windows')) {
      LoggerUtil.debug(`Operating System type: ${DesktopOsType.Windows}`)
      return DesktopOsType.Windows
    } else if (userAgent.includes('Mac OS')) {
      LoggerUtil.debug(`Operating System type: ${DesktopOsType.Mac}`)
      return DesktopOsType.Mac
    }

    return DesktopOsType.Unknown
  }

  /**
   * Gets whether the app is currently running in PWA mode.
   * @returns {Boolean} True if the app is currently running in PWA mode.
   */
  static getIsPwa () {
    const isPwa = navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
    LoggerUtil.debug(`Is PWA: ${isPwa}`)
    return isPwa
  }
}

export default DeviceUtil
